// ExportStockToExcel.ts
import ExcelJS from 'exceljs'
import { saveAs } from 'file-saver'
import moment from 'moment'
import { toBuddhistYear } from 'utils/app.utils'
import { forceTextOnDateCells } from 'utils/excel.utils'

export interface StockRow {
    documentNo: string
    subject: string
    lotNo: string
    itemTypeName: string
    itemName: string
    expireDate: string | null
    qty: number
    itemUnitName: string
    costPrice: number
    salePrice: number
    total: number
    createdBy: string
}


export interface StockReportData {
    branchName: string
    dateStart: string
    dateEnd: string
    productType?: string
    product?: string
    rows: StockRow[],
    t: any
}

export async function ExportStockToExcel(report: StockReportData) {
    const wb = new ExcelJS.Workbook()
    const ws = wb.addWorksheet(report.t(`REPORT.REPORT_NAME.STOCK`), {
        pageSetup: { orientation: 'landscape' }
    })

    ws.properties.defaultRowHeight = 18
    ws.columns = [
        { width: 5 }, // A #
        { width: 15 }, // B เลขที่เอกสาร
        { width: 25 }, // C ชื่อเอกสาร
        { width: 12 }, // D รหัสล็อต
        { width: 15 }, // E ประเภทสินค้า
        { width: 25 }, // F รายการสินค้า
        { width: 15 }, // G วันหมดอายุ
        { width: 12 }, // H จำนวนคงเหลือ
        { width: 10 }, // I หน่วย
        { width: 18 }, // J ราคาทุน(บาท)/หน่วย
        { width: 15 }, // K ราคาขาย(บาท)
        { width: 18 }, // L ราคาทุนรวม(บาท)
        { width: 20 }, // M ผู้บันทึก
    ]

    let r = 1

    ws.mergeCells(r, 1, r, 13)
    ws.getCell(r, 1).value = `${report.t(`REPORT.REPORT_NAME.STOCK`)}`
    ws.getCell(r, 1).font = { size: 16, bold: true }
    ws.getCell(r, 1).alignment = { horizontal: 'center' }
    r++

    ws.mergeCells(r, 1, r, 13)
    ws.getCell(r, 1).value =
        `${report.t('REPORT.FILTER.BRANCH')} : ${report.branchName}       ` +
        `${report.t('REPORT.FILTER.DATE_START')} : ${report.dateStart}     ` +
        `${report.t('REPORT.FILTER.DATE_END')} : ${report.dateEnd}` +
        (report.productType ? `    ${report.t('REPORT.FILTER.PRODUCT_TYPE')} : ${report.productType}` : '') +
        (report.product ? `    ${report.t('REPORT.FILTER.PRODUCT')} : ${report.product}` : '')
    ws.getCell(r, 1).font = { size: 12 }
    ws.getCell(r, 1).alignment = { horizontal: 'left' }
    r += 2

    const headerRow = ws.addRow([
        '#',
        report.t('REPORT.TABLE.STOCK.DOCUMENT_NO'),
        report.t('REPORT.TABLE.STOCK.SUBJECT'),
        report.t('REPORT.TABLE.STOCK.LOT_NO'),
        report.t('REPORT.TABLE.STOCK.PRODUCT_TYPE'),
        report.t('REPORT.TABLE.STOCK.PRODUCT'),
        report.t('REPORT.TABLE.STOCK.EXPIRE_DATE'),
        report.t('REPORT.TABLE.STOCK.QTY'),
        report.t('REPORT.TABLE.STOCK.UNIT'),
        report.t('REPORT.TABLE.STOCK.COST_PRICE'),
        report.t('REPORT.TABLE.STOCK.SALE_PRICE'),
        report.t('REPORT.TABLE.STOCK.TOTAL_PRICE'),
        report.t('REPORT.TABLE.STOCK.CREATEDBY')
    ])
    headerRow.eachCell(cell => {
        cell.font = { bold: true }
        cell.alignment = { horizontal: 'center', vertical: 'middle' }
        cell.border = {
            top: { style: 'thin' },
            left: { style: 'thin' },
            bottom: { style: 'thin' },
            right: { style: 'thin' }
        }
    })
    r++

    if (report.rows.length === 0) {
        const noDataRow = ws.addRow([])
        noDataRow.height = 20
        ws.mergeCells(noDataRow.number, 1, noDataRow.number, ws.columns.length)
        const cell = ws.getCell(noDataRow.number, 1)
        cell.value = report.t('NOT_FOUND')
        cell.font = { italic: true }
        cell.alignment = { horizontal: 'center', vertical: 'middle' }
        forceTextOnDateCells(wb)
        const buf = await wb.xlsx.writeBuffer()
        saveAs(new Blob([buf]), `Stock_Report_${moment().format('YYYYMMDD')}.xlsx`)
        return
    }
    report.rows.forEach((row, idx) => {
        const excelRow = ws.addRow([
            idx + 1,
            row.documentNo,
            row.subject,
            row.lotNo,
            row.itemTypeName,
            row.itemName,
            row.expireDate
                ? toBuddhistYear(moment(row.expireDate), 'DD/MM/YYYY')
                : '-',
            row.qty,
            row.itemUnitName || '-',
            row.costPrice,
            row.salePrice,
            row.total,
            row.createdBy
        ])
        excelRow.eachCell((cell, col) => {
            if ([8, 10, 11, 12].includes(col)) {
                cell.numFmt = '#,##0.00'
                cell.alignment = { horizontal: 'right' }
            } else if (col === 7) {
                cell.alignment = { horizontal: 'center' }
            } else {
                cell.alignment = { horizontal: 'left' }
            }
            cell.border = {
                top: { style: 'thin' },
                left: { style: 'thin' },
                bottom: { style: 'thin' },
                right: { style: 'thin' }
            }
        })
    })

    forceTextOnDateCells(wb)
    const buf = await wb.xlsx.writeBuffer()
    saveAs(
        new Blob([buf]),
        `${report.t(`REPORT.REPORT_NAME.STOCK`)}_${moment().format('YYYYMMDD')}.xlsx`
    )
}
